import type { ForecastRain } from '../api/forecastService';
import type { Session } from '../api/eventsService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const LEGACY_SESSION_NAME_HINTS: Record<string, string[]> = {
  FREE: ['free practice', 'fp'],
  qualifications: ['qualifying', 'qualification'],
  sprint_qualifications: ['sprint qualifying', 'sprint qualification'],
  sprint: ['sprint'],
  race: ['race'],
};

/**
 * Canonical string stored in forecast.session_type for non-legacy events.
 */
export function sessionTypeValueForSession(session: Session, circuitTz: string): string {
  const name = session.session_name?.trim() || 'Session';
  try {
    const tz = circuitTz || 'UTC';
    const start = dayjs.utc(session.start_time).tz(tz);
    const end = dayjs.utc(session.end_time).tz(tz);
    if (!start.isValid() || !end.isValid()) return name;

    const sameDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD');
    const range = sameDay
      ? `${start.format('ddd D MMM YYYY, HH:mm')} – ${end.format('HH:mm')}`
      : `${start.format('ddd D MMM YYYY, HH:mm')} – ${end.format('ddd D MMM YYYY, HH:mm')}`;

    return `${name} (${range})`;
  } catch {
    return name;
  }
}

function pickSessionContainingTime(sessions: Session[], timeIso: string): Session | null {
  const timeMs = new Date(timeIso).getTime();
  const containing = sessions.filter((session) => {
    const startMs = new Date(session.start_time).getTime();
    const endMs = new Date(session.end_time).getTime();
    return timeMs >= startMs && timeMs <= endMs;
  });

  if (containing.length === 1) return containing[0];
  if (containing.length > 1) {
    return [...containing].sort(
      (a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime(),
    )[0];
  }

  let closest: Session | null = null;
  let closestDiff = Infinity;
  for (const session of sessions) {
    const endMs = new Date(session.end_time).getTime();
    if (timeMs > endMs) continue;
    const startMs = new Date(session.start_time).getTime();
    const diff = Math.abs(timeMs - startMs);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = session;
    }
  }
  return closest;
}

export function findSessionForRainForecast(
  sessionType: string | null,
  sessions: Session[],
  circuitTz: string,
  forecastValueDate?: string,
): Session | null {
  if (!sessionType || sessions.length === 0) return null;

  for (const session of sessions) {
    const canonical = sessionTypeValueForSession(session, circuitTz);
    if (sessionType === canonical || sessionType.startsWith(`${canonical} (`)) {
      return session;
    }
  }

  const namePrefix = sessionType.split(' (')[0]?.trim();
  if (namePrefix) {
    const byName = sessions.filter((s) => s.session_name?.trim() === namePrefix);
    if (byName.length === 1) return byName[0];
    if (byName.length > 1 && forecastValueDate) {
      return pickSessionContainingTime(byName, forecastValueDate);
    }
  }

  const hints = LEGACY_SESSION_NAME_HINTS[sessionType];
  if (hints) {
    const candidates = sessions.filter((session) => {
      const name = session.session_name.toLowerCase();
      return hints.some((hint) => name.includes(hint));
    });
    if (candidates.length === 1) return candidates[0];
    if (candidates.length > 1 && forecastValueDate) {
      return pickSessionContainingTime(candidates, forecastValueDate);
    }
  }

  return null;
}

const SESSION_END_PARSE_FORMAT = 'D MMM YYYY, HH:mm';

function parseClockInTimezone(dateTimeStr: string, circuitTz: string): Date | null {
  try {
    const parsed = dayjs.tz(dateTimeStr, SESSION_END_PARSE_FORMAT, circuitTz || 'UTC');
    return parsed.isValid() ? parsed.toDate() : null;
  } catch {
    return null;
  }
}

function parseSessionEndFromSessionType(sessionType: string, circuitTz: string): Date | null {
  const rangeMatch = sessionType.match(/\(([^)]+)\)\s*$/);
  if (!rangeMatch) return null;

  const parts = rangeMatch[1].split(' – ');
  if (parts.length !== 2) return null;

  const startPart = parts[0].trim();
  const endPart = parts[1].trim();

  // Strip weekday tokens. dayjs.tz + customParseFormat throws RangeError on
  // `ddd D MMM YYYY` (and on single-digit days parsed with that format).
  const endDateMatch = endPart.match(/(\d{1,2} [A-Z][a-z]{2} \d{4}),\s*(\d{1,2}:\d{2})/);
  if (endDateMatch) {
    return parseClockInTimezone(`${endDateMatch[1]}, ${endDateMatch[2]}`, circuitTz);
  }

  const timeMatch = endPart.match(/(\d{1,2}:\d{2})$/);
  const startDateMatch = startPart.match(/(\d{1,2} [A-Z][a-z]{2} \d{4})/);
  if (!timeMatch || !startDateMatch) return null;

  return parseClockInTimezone(`${startDateMatch[1]}, ${timeMatch[1]}`, circuitTz);
}

function getSessionEndTime(
  forecast: ForecastRain,
  sessions: Session[],
  circuitTimezone: string,
): Date | null {
  if (!forecast.session_type) return null;

  const session = findSessionForRainForecast(
    forecast.session_type,
    sessions,
    circuitTimezone,
    forecast.value_date,
  );
  if (session) return new Date(session.end_time);

  return parseSessionEndFromSessionType(forecast.session_type, circuitTimezone);
}

export function isRainForecastExpiredForAnchor(
  forecast: ForecastRain,
  anchorTime: Date,
  sessions: Session[],
  circuitTimezone: string,
): boolean {
  const sessionEnd = getSessionEndTime(forecast, sessions, circuitTimezone);
  if (!sessionEnd) return false;
  return anchorTime.getTime() > sessionEnd.getTime();
}

export interface ResolveActiveRainForecastParams {
  forecasts: ForecastRain[];
  anchorTime: Date | null;
  sessions?: Session[];
  circuitTimezone?: string;
  requireActive?: boolean;
  /** Root users may see forecasts with a value_date in the future (matches RainForecastsPage). */
  isRoot?: boolean;
  /** Wall-clock time for session-expiry checks; pass a ticking value (e.g. useMinuteClock). */
  currentTime?: Date;
}

function getSessionCheckTime(anchorTime: Date | null, currentTime: Date): Date {
  if (!anchorTime) return currentTime;
  return new Date(Math.min(anchorTime.getTime(), currentTime.getTime()));
}

/**
 * Resolve the latest rain forecast to display at a given anchor time.
 * Hides the forecast once its linked session has ended.
 */
export function resolveActiveRainForecast({
  forecasts,
  anchorTime,
  sessions = [],
  circuitTimezone = 'UTC',
  requireActive = false,
  isRoot = false,
  currentTime = new Date(),
}: ResolveActiveRainForecastParams): ForecastRain | null {
  if (!forecasts.length) return null;

  let candidates = forecasts;
  if (requireActive) {
    candidates = candidates.filter((f) => f.is_active);
  }

  if (anchorTime) {
    const anchorMs = anchorTime.getTime();
    candidates = candidates.filter((f) => new Date(f.value_date).getTime() <= anchorMs);
  }

  // Non-root users: hide forecasts with a value_date in the future
  if (!isRoot) {
    const nowMs = dayjs.utc(currentTime).valueOf();
    candidates = candidates.filter((f) => dayjs.utc(f.value_date).valueOf() <= nowMs);
  }

  if (!candidates.length) return null;

  const latest = [...candidates].sort(
    (a, b) => new Date(b.value_date).getTime() - new Date(a.value_date).getTime(),
  )[0];

  const sessionCheckTime = getSessionCheckTime(anchorTime, currentTime);
  if (isRainForecastExpiredForAnchor(latest, sessionCheckTime, sessions, circuitTimezone)) {
    return null;
  }

  return latest;
}
